import React from "react";
import { Download, Monitor, Video } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SFONDI = [
  {
    id: "sfondo-ufficio",
    titolo: "Ufficio A.C.D.T. — Sala Direzione",
    descrizione:
      "Sfondo elegante con logo A.C.D.T. inciso, dominio istituzionale e ambiente ufficio professionale. Perfetto per meeting con clienti e presentazioni ufficiali.",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/job_acdt-hub/artifacts/rvhjhnmq_sfondo%20zoom%204.png",
    preview: "https://customer-assets-4nw71qhi.emergentagent.net/job_acdt-hub/artifacts/rvhjhnmq_sfondo%20zoom%204.png",
    formato: "PNG · 1920×1080 · ~2 MB",
    filename: "ACDT-Zoom-Ufficio-Direzione.png",
    badge: "Consigliato",
    badgeColor: "bg-emerald-500 text-white",
  },
  {
    id: "sfondo-blur",
    titolo: "Blur — Sfondo sfocato bokeh",
    descrizione:
      "Effetto sfocato con gradiente navy-oro. Ideale quando vuoi mettere il focus solo su di te, senza dettagli distraenti.",
    url: `${API}/api/download/sfondo-meeting/blur`,
    preview: `${API}/api/download/sfondo-meeting/blur`,
    formato: "PNG · 1920×1080",
    filename: "ACDT-Zoom-Blur.png",
    badge: "Nuovo",
    badgeColor: "bg-[#f59e0b] text-[#0a1020]",
  },
  {
    id: "sfondo-minimal",
    titolo: "Minimal — Bianco professionale",
    descrizione:
      "Sfondo pulito con barra navy in basso e logo A.C.D.T. discreto. Perfetto per meeting B2B e presentazioni corporate.",
    url: `${API}/api/download/sfondo-meeting/minimal`,
    preview: `${API}/api/download/sfondo-meeting/minimal`,
    formato: "PNG · 1920×1080",
    filename: "ACDT-Zoom-Minimal.png",
    badge: "Nuovo",
    badgeColor: "bg-[#f59e0b] text-[#0a1020]",
  },
  {
    id: "sfondo-eventi",
    titolo: "Eventi — Sfondo istituzionale",
    descrizione:
      "Sfondo per webinar ed eventi ufficiali: fascia navy top con branding evento A.C.D.T. e hashtag social. Massima visibilità del brand.",
    url: `${API}/api/download/sfondo-meeting/eventi`,
    preview: `${API}/api/download/sfondo-meeting/eventi`,
    formato: "PNG · 1920×1080",
    filename: "ACDT-Zoom-Eventi.png",
    badge: "Nuovo",
    badgeColor: "bg-[#f59e0b] text-[#0a1020]",
  },
];

/**
 * Sezione dashboard per scaricare sfondi meeting (Zoom, Teams, Meet)
 * brandizzati A.C.D.T.
 */
export default function SfondiMeetingSection() {
  const trackDownload = (id) => {
    try {
      window.posthog?.capture?.("sfondo_meeting_download", { id });
    } catch { /* posthog non disponibile */ }
  };

  return (
    <div className="space-y-6" data-testid="sfondi-meeting-section">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border-2 border-[#0a1f55]/20 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] p-6 md:p-8 text-white">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="h-14 w-14 shrink-0 rounded-2xl bg-[#f59e0b] text-[#0a1020] flex items-center justify-center shadow-lg">
            <Video className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#f59e0b]">
              Risorse per meeting
            </div>
            <h1 className="mt-1 text-[22px] md:text-[26px] font-extrabold leading-tight">
              Sfondi ufficiali per Zoom, Teams e Meet
            </h1>
            <p className="mt-2 text-[13.5px] md:text-[14.5px] text-white/85 leading-relaxed max-w-2xl">
              Usa gli sfondi virtuali A.C.D.T. nei tuoi meeting professionali per trasmettere subito
              autorevolezza e appartenenza all&apos;Associazione. Formati compatibili con tutte le
              principali piattaforme di videoconferenza.
            </p>
          </div>
        </div>
      </div>

      {/* Sfondi */}
      <div className="grid md:grid-cols-2 gap-5">
        {SFONDI.map((s) => (
          <div
            key={s.id}
            className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col"
            data-testid={`sfondo-card-${s.id}`}
          >
            <div className="relative bg-slate-900 aspect-video overflow-hidden">
              <img
                src={s.preview}
                alt={s.titolo}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-[0.15em]">
                <Monitor className="h-3 w-3" />
                {s.formato}
              </div>
              {s.badge && (
                <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10.5px] font-extrabold uppercase tracking-[0.15em] ${s.badgeColor}`}>
                  {s.badge}
                </div>
              )}
            </div>
            <div className="p-5 md:p-6 flex-1 flex flex-col">
              <h3 className="font-extrabold text-[#0a1f55] text-[15px] md:text-[16px]">
                {s.titolo}
              </h3>
              <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed flex-1">
                {s.descrizione}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={s.url}
                  download={s.filename}
                  onClick={() => trackDownload(s.id)}
                  data-testid={`sfondo-download-${s.id}`}
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-extrabold text-[13px] transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Scarica
                </a>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-xl border border-slate-300 hover:border-[#102a6b] text-[#0a1f55] font-bold text-[13px] transition-colors"
                >
                  Anteprima
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Istruzioni utilizzo */}
      <div className="rounded-2xl border border-[#f59e0b]/40 bg-gradient-to-r from-[#fff7ed] via-white to-[#fef3c7] p-5">
        <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-[#b45309] mb-2">
          Come usarlo
        </div>
        <ol className="space-y-1.5 text-[13px] text-slate-700 leading-relaxed">
          <li><strong>1.</strong> Scarica il file PNG cliccando sul bottone qui sopra</li>
          <li><strong>2.</strong> Apri Zoom → <em>Impostazioni</em> → <em>Sfondo e effetti</em> → <em>+ Aggiungi immagine</em></li>
          <li><strong>3.</strong> Seleziona il file scaricato. Su Teams/Meet il percorso è simile</li>
          <li><strong>4.</strong> Attiva la webcam: lo sfondo A.C.D.T. sarà visibile durante il meeting</li>
        </ol>
      </div>
    </div>
  );
}
